import { useMemo, useState } from "react";
import { Select } from "@headlessui/react";
import { get, ref, remove, update } from "firebase/database";
import SourceForm from "../components/SourceForm";
import ConfirmDialog from "../components/ConfirmDialog";
import { useFirebase } from "../FbContex";
import { db } from "../firebase";
import { ISource } from "../@types/source";
import { WithId } from "../@types/room";
import {
  allSources,
  allNodes,
  nextOrder,
  kindOf,
  nodesOfSource,
  swapOrder,
  usedAddrs,
} from "../lib/selectors";
import { prune, writeTargets } from "../lib/node";
import { SourceKind } from "../enum";
import { AdminSection, OrderCell, ToggleCell } from "../components/table/Cells";
import {
  BTN_PRIMARY,
  SELECT_CLASS,
  TD,
  TD_HEAD,
  TH,
} from "../components/tableStyles";

const BAD_KEY = /[.$#[\]/]/;
// "engines" is a tombstone: the abandoned admin path, kept so nobody
// creates a source there while stale rows may still sit under it.
const RESERVED = ["sources", "engines", "rooms", "devices", "users"];

export default function Sources() {
  const { sources, sourcesReady, state, devices } = useFirebase();
  const [source, setSource] = useState<ISource | null>(null);
  const [target, setTarget] = useState<WithId<ISource> | null>(null);
  const [mode, setMode] = useState<"move" | "purge">("move");
  const [moveTo, setMoveTo] = useState("");
  const [sensorTarget, setSensorTarget] = useState<WithId<ISource> | null>(null);
  const [importing, setImporting] = useState(false);
  const [busy, setBusy] = useState(false);

  const list = useMemo(() => allSources(sources), [sources]);

  const liveCount = (id: string) => Object.keys(state[id] ?? {}).length;

  /**
   * Derived from the devices, written explicitly. Visible whenever there is
   * anything to do, so the empty database and the top-up case are one button
   * rather than two modes.
   */
  const missing = useMemo(
    () =>
      usedAddrs(devices).filter(
        (a) => !BAD_KEY.test(a) && !RESERVED.includes(a) && !sources[a]
      ),
    [devices, sources]
  );

  const owned = target ? nodesOfSource(devices, target.id) : [];
  // Like-for-like: moving a readout between two engines is sane, moving it
  // from /data to /nodes is not -- the key namespaces are different.
  const others = list.filter(
    (e) => e.id !== target?.id && (!target || kindOf(e) === kindOf(target))
  );

  /**
   * The reason source delete differs from room delete: a state key that exists
   * on one source almost certainly does not exist on another.
   */
  const targetKeys = new Set(Object.keys(state[moveTo] ?? {}));
  const broken = moveTo ? owned.filter((n) => !targetKeys.has(n.stateKey)) : [];

  const blocked = mode === "move" && owned.length > 0 && !moveTo;

  const confirmDelete = () => {
    if (!target) return;
    setBusy(true);

    // One atomic multi-path write, so there is never a half state.
    const updates: { [path: string]: any } = { [`sources/${target.id}`]: null };
    owned.forEach((n) => {
      if (mode === "purge") updates[`devices/${n.id}`] = null;
      else updates[`devices/${n.id}/addr`] = moveTo;
    });

    const op = owned.length
      ? update(ref(db), updates)
      : remove(ref(db, `sources/${target.id}`));

    op.catch((err) => alert(err.message)).finally(() => {
      setBusy(false);
      setTarget(null);
      setMoveTo("");
      setMode("move");
    });
  };

  /**
   * Harvest the existing LAN ips with a one-off get(): at first run nothing is
   * subscribed yet, so ctx.state is empty and reading from it would drop them.
   * The firmware publishes it into its own tree; nothing has ever written
   * /data/ip_*, so that path is not consulted.
   */
  const runImport = async () => {
    setBusy(true);
    try {
      const written = allNodes(devices).flatMap((n) =>
        writeTargets(n).map((t) => t.addr)
      );
      const updates: { [path: string]: any } = {};
      let order = nextOrder(list);
      for (const addr of missing) {
        const own = (await get(ref(db, addr))).val() ?? {};
        updates[`sources/${addr}`] = prune({
          label: addr,
          ip: typeof own.ip === "string" ? own.ip : undefined,
          kind: written.includes(addr) ? SourceKind.ENGINE : SourceKind.TELEMETRY,
          order,
        });
        order += 10;
      }
      await update(ref(db), updates);
      setImporting(false);
    } catch (err: any) {
      alert(err.message);
    } finally {
      setBusy(false);
    }
  };

  const changeKind = (e: WithId<ISource>) => {
    // Turning it into telemetry while writers exist is not a silent toggle.
    if (
      kindOf(e) === SourceKind.ENGINE &&
      nodesOfSource(devices, e.id).some((n) => writeTargets(n).length)
    )
      return setSensorTarget(e);
    update(ref(db, `sources/${e.id}`), {
      kind:
        kindOf(e) === SourceKind.TELEMETRY
          ? SourceKind.ENGINE
          : SourceKind.TELEMETRY,
    });
  };

  const sensorWriters = sensorTarget
    ? nodesOfSource(devices, sensorTarget.id).filter((n) => writeTargets(n).length)
    : [];

  return (
    <>
      <SourceForm source={source} close={() => setSource(null)} />

      <ConfirmDialog
        open={importing}
        title={`Add ${missing.length} source${missing.length > 1 ? "s" : ""}?`}
        confirmLabel="Import"
        danger={false}
        busy={busy}
        onConfirm={runImport}
        onCancel={() => setImporting(false)}
      >
        <p>Derived from the addresses your devices already reference.</p>
        <ul className="list-disc list-inside opacity-70">
          {missing.map((a) => (
            <li key={a}>
              /{a} &mdash; {nodesOfSource(devices, a).length} devices
            </li>
          ))}
        </ul>
        <p className="opacity-60">
          Existing sources are never touched. The LAN ip is read from each
          path&apos;s own tree, where the firmware publishes it.
        </p>
      </ConfirmDialog>

      <ConfirmDialog
        open={!!sensorTarget}
        title={`Change ${sensorTarget?.label ?? "source"} to telemetry?`}
        confirmLabel="Change kind"
        danger={false}
        onConfirm={() => {
          update(ref(db, `sources/${sensorTarget!.id}`), { sensorOnly: true });
          setSensorTarget(null);
        }}
        onCancel={() => setSensorTarget(null)}
      >
        <p>
          {sensorWriters.length} device{sensorWriters.length > 1 ? "s" : ""} write
          to this source:
        </p>
        <ul className="list-disc list-inside opacity-70">
          {sensorWriters.map((n) => (
            <li key={n.id}>{n.title}</li>
          ))}
        </ul>
        <p className="opacity-60">
          Telemetry is written by the boards, not by the app. These devices
          keep rendering, but their writes will be dropped and the form will
          refuse new ones.
        </p>
      </ConfirmDialog>

      <ConfirmDialog
        open={!!target}
        title={`Delete ${target?.label ?? "source"}?`}
        busy={busy}
        confirmLabel={
          mode === "move" && broken.length
            ? `Move anyway (${broken.length} broken)`
            : "Delete"
        }
        confirmDisabled={blocked}
        onConfirm={confirmDelete}
        onCancel={() => {
          setTarget(null);
          setMoveTo("");
          setMode("move");
        }}
      >
        {owned.length === 0 ? (
          <p>
            The live data at <code>/{target?.id}</code> is not touched. Only the
            metadata row is removed.
          </p>
        ) : (
          <>
            <p>
              {owned.length} device{owned.length > 1 ? "s" : ""} point at{" "}
              <code>/{target?.id}</code>.
            </p>
            <label className="flex gap-2 items-start">
              <input
                type="radio"
                className="mt-1"
                checked={mode === "move"}
                onChange={() => setMode("move")}
              />
              <span className="flex-1">
                <span className="block mb-1">Move them to another source</span>
                <Select
                  value={moveTo}
                  disabled={mode !== "move"}
                  onChange={(e) => setMoveTo(e.target.value)}
                  className={SELECT_CLASS}
                >
                  <option value="">--select a source--</option>
                  {others.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.label || e.id}
                    </option>
                  ))}
                </Select>
                {target && (
                  <span className="block text-xs opacity-50 mt-1">
                    Only other {kindOf(target)} sources are offered.
                  </span>
                )}
              </span>
            </label>

            {mode === "move" && moveTo && (
              <ul className="space-y-1">
                {owned.map((n) => {
                  const ok = targetKeys.has(n.stateKey);
                  return (
                    <li key={n.id} className={ok ? "" : "text-red-400"}>
                      <i
                        className={
                          ok
                            ? "fas fa-circle-check text-green-500 mr-2"
                            : "fas fa-circle-xmark mr-2"
                        }
                      />
                      {n.title}
                      {!ok && ` -- ${n.stateKey} not found under /${moveTo}`}
                    </li>
                  );
                })}
              </ul>
            )}

            <label className="flex gap-2 items-center">
              <input
                type="radio"
                checked={mode === "purge"}
                onChange={() => setMode("purge")}
              />
              <span>Delete the {owned.length} devices too</span>
            </label>

            {mode === "move" && others.length === 0 && (
              <p className="text-red-400">
                There is no other source of the same kind to move them to.
                Create one first, or choose to delete the devices.
              </p>
            )}
          </>
        )}
      </ConfirmDialog>

      <AdminSection
        title="Sources"
        actions={
          <>
            {sourcesReady && missing.length > 0 && (
              <button
                className={BTN_PRIMARY}
                type="button"
                onClick={() => setImporting(true)}
              >
                Import {missing.length} from devices
              </button>
            )}
            <button
              className={BTN_PRIMARY}
              type="button"
              onClick={() => setSource({} as ISource)}
            >
              Add new
            </button>
          </>
        }
      >
        <thead>
          <tr>
            <th className={TH}>source</th>
            <th className={TH}>ip</th>
            <th className={TH}>telemetry</th>
            <th className={TH}>devices</th>
            <th className={TH}>live keys</th>
            <th className={TH}>enabled</th>
            <th className={TH}>order</th>
            <th className={TH}>action</th>
          </tr>
        </thead>
        <tbody>
          {list.length === 0 && (
            <tr>
              <td className={`${TD} opacity-50`} colSpan={8}>
                {sourcesReady ? "No sources yet." : "Loading..."}
              </td>
            </tr>
          )}
          {list.map((e, i) => {
            const keys = liveCount(e.id);
            return (
              <tr key={e.id} className={e.disabled ? "opacity-50" : ""}>
                <th className={TD_HEAD}>
                  <div>{e.label || e.id}</div>
                  <code className="opacity-50 font-normal">/{e.id}</code>
                </th>
                <td className={TD}>
                  {kindOf(e) === SourceKind.TELEMETRY ? (
                    <span className="opacity-40">n/a</span>
                  ) : (
                    e.ip || <span className="opacity-40">--</span>
                  )}
                </td>
                <ToggleCell
                  checked={kindOf(e) === SourceKind.TELEMETRY}
                  onChange={() => changeKind(e)}
                />
                <td className={TD}>{nodesOfSource(devices, e.id).length}</td>
                <td className={TD}>
                  {keys ? (
                    keys
                  ) : (
                    <span className="text-red-500">no live data</span>
                  )}
                </td>
                <ToggleCell
                  checked={!e.disabled}
                  onChange={() =>
                    update(ref(db, `sources/${e.id}`), {
                      disabled: !e.disabled || null,
                    })
                  }
                />
                <OrderCell
                  index={i}
                  count={list.length}
                  onMove={(dir) => swapOrder("sources", list, i, dir)}
                />
                <td className={TD}>
                  <i
                    onClick={() => setSource({ ...e })}
                    className="fas fa-pencil text-emerald-500 mr-4 cursor-pointer hover:opacity-75"
                  />
                  <i
                    onClick={() => setTarget(e)}
                    className="fas fa-trash text-red-500 cursor-pointer hover:opacity-75"
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </AdminSection>
    </>
  );
}
